import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Select, { selectClasses } from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import React, { useState } from 'react';
import { SORT_OPTIONS, STATUS_FILTER_OPTIONS, DEFAULT_FILTERS } from '../constants';
import { FilterState } from '../types';
import { DatePickerField } from '../../../components/ui/DatePickerField';
import { JoySelectField } from '../../../components/ui/JoySelectField';

const formatToYYYYMMDD = (date: Date | null): string => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseYYYYMMDD = (str: string): Date | null => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

// Опции для направления сортировки
const SORT_ORDER_OPTIONS = [
  { value: 'asc', label: 'По возрастанию' },
  { value: 'desc', label: 'По убыванию' },
];

interface SearchAndFiltersProps {
  searchQuery: string;
  onSearchChange: (q: string) => void;
  searchType: 'organization_name' | 'department_name' | 'specialist_query' | 'student_query';
  onSearchTypeChange: (type: 'organization_name' | 'department_name' | 'specialist_query' | 'student_query') => void;
  filters: FilterState;
  appliedFilters: FilterState;
  onFiltersChange: (f: FilterState) => void;
  onApply: () => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

const getPlaceholderText = (type: string) => {
  switch (type) {
    case 'organization_name':
      return 'Введите название организации...';
    case 'department_name':
      return 'Введите название отдела...';
    case 'specialist_query':
      return 'Введите ФИО или email специалиста...';
    case 'student_query':
      return 'Введите ФИО или login обучающегося...';
    default:
      return 'Поиск...';
  }
};

const SearchAndFilters: React.FC<SearchAndFiltersProps> = ({
  searchQuery,
  onSearchChange,
  searchType,
  onSearchTypeChange,
  filters,
  appliedFilters,
  onFiltersChange,
  onApply,
  onReset,
  hasActiveFilters,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isApplyDisabled = JSON.stringify(filters) === JSON.stringify(appliedFilters);
  const isResetDisabled =
    JSON.stringify(filters) === JSON.stringify(DEFAULT_FILTERS) &&
    JSON.stringify(appliedFilters) === JSON.stringify(DEFAULT_FILTERS);

  return (
    <div>
      {/* ─── Верхняя панель (Объединенный Поиск и Кнопка Фильтров) ─── */}
      <div className="flex flex-col sm:flex-row gap-3 items-end">

        {/* Гибридный блок: Поле ввода + Выпадающий список в одной рамке */}
        <div className="flex-1 w-full flex flex-col">
          <label className="text-[13px] font-semibold text-gray-700 block mb-[3px] h-[17px] leading-tight">
            Поисковый запрос
          </label>

          {/* Заменили фокусные синие кольца focus-within:ring-2/ring-blue-500 на мягкое изменение рамки focus-within:border-gray-400 */}
          <div className="w-full flex items-stretch border border-gray-300 rounded-lg bg-white shadow-sm transition-all focus-within:border-gray-400 h-[42px] box-border overflow-hidden">

            {/* Левая часть: Инпут для ввода текста */}
            <div className="relative flex-1 h-full">
              <SearchOutlinedIcon
                fontSize="small"
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                placeholder={getPlaceholderText(searchType)}
                className="w-full h-full pl-10 pr-4 bg-transparent border-none outline-none text-sm text-gray-800 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                value={searchQuery || ''}
                onChange={e => onSearchChange(e.target.value)}
              />
            </div>

            {/* Правая часть: Выпадающий список критериев Joy Select, приклеенный вплотную */}
            <div className="w-[150px] sm:w-[170px] shrink-0 h-full border-l border-gray-300">
              <Select<string>
                value={searchType}
                onChange={(_event, newValue) => onSearchTypeChange(newValue as any)}
                indicator={<KeyboardArrowDown />}
                slotProps={{
                  listbox: {
                    sx: {
                      maxHeight: 240,
                      overflow: 'auto',
                      zIndex: 9999, // Поверх раскрывающейся панели
                    },
                  },
                }}
                sx={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '0px',
                  backgroundColor: 'transparent',
                  '--joy-palette-neutral-outlinedBorder': 'transparent',
                  '&:hover': {
                    backgroundColor: '#f9fafb', // Лёгкий ховер эффект на выпадающем списке
                  },
                  [`& .${selectClasses.indicator}`]: {
                    transition: '0.2s',
                    [`&.${selectClasses.expanded}`]: {
                      transform: 'rotate(-180deg)',
                    },
                  },
                }}
              >
                <Option value="organization_name">Организация</Option>
                <Option value="department_name">Отдел</Option>
                <Option value="specialist_query">Специалист</Option>
                <Option value="student_query">Обучающийся</Option>
              </Select>
            </div>

          </div>
        </div>

        {/* Кнопка "Фильтры" */}
        <div className="w-full sm:w-auto shrink-0">
          <span className="block mb-[3px] h-[17px] opacity-0 select-none pointer-events-none" aria-hidden="true">
            Заглушка
          </span>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-5 h-[42px] border rounded-lg text-sm font-medium shadow-sm transition-colors box-border ${
              hasActiveFilters || isExpanded
                ? 'border-blue-400 bg-blue-50 text-blue-700'
                : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50'
            }`}
          >
            <TuneOutlinedIcon fontSize="small" />
            Фильтры
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
            )}
          </button>
        </div>
      </div>

      {/* ─── Выпадающая панель с плавным раскрытием ─── */}
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0 pointer-events-none'
        }`}
      >
        <div className="overflow-hidden">
          <div className="pt-3">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 items-start">

                {/* Дополнительный поиск по отделу */}
                <div className="flex flex-col gap-1.5 w-full">
                  <label className="text-[13px] font-semibold text-gray-700">Отдел</label>
                  <input
                    type="text"
                    placeholder="Название отдела..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white shadow-sm h-[42px] box-border disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                    value={filters.department_name || ''}
                    disabled={searchType === 'department_name'}
                    onChange={e => onFiltersChange({ ...filters, department_name: e.target.value })}
                  />
                </div>

                {/* Дополнительный поиск по ФИО специалиста */}
                <div className="flex flex-col gap-1.5 w-full">
                  <label className="text-[13px] font-semibold text-gray-700">Ответственный специалист</label>
                  <input
                    type="text"
                    placeholder="ФИО или email специалиста..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white shadow-sm h-[42px] box-border disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                    value={filters.specialist_query || ''}
                    disabled={searchType === 'specialist_query'}
                    onChange={e => onFiltersChange({ ...filters, specialist_query: e.target.value })}
                  />
                </div>

                {/* Дополнительный поиск по ФИО/логину студента */}
                <div className="flex flex-col gap-1.5 w-full">
                  <label className="text-[13px] font-semibold text-gray-700">Обучающийся</label>
                  <input
                    type="text"
                    placeholder="ФИО или login студента..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white shadow-sm h-[42px] box-border disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                    value={filters.student_query || ''}
                    disabled={searchType === 'student_query'}
                    onChange={e => onFiltersChange({ ...filters, student_query: e.target.value })}
                  />
                </div>

                {/* Статус обучения */}
                <JoySelectField
                  label="Статус обучения"
                  placeholder="Выберите статус"
                  value={filters.overall_status || null}
                  onChange={val => onFiltersChange({ ...filters, overall_status: val || '' })}
                  options={STATUS_FILTER_OPTIONS}
                />

                {/* Дедлайн от */}
                <DatePickerField
                  label="Дедлайн от"
                  selected={parseYYYYMMDD(filters.nearest_deadline_from)}
                  maxDate={parseYYYYMMDD(filters.nearest_deadline_to)}
                  onChange={d => onFiltersChange({ ...filters, nearest_deadline_from: formatToYYYYMMDD(d) })}
                />

                {/* Дедлайн до */}
                <DatePickerField
                  label="Дедлайн до"
                  selected={parseYYYYMMDD(filters.nearest_deadline_to)}
                  minDate={parseYYYYMMDD(filters.nearest_deadline_from)}
                  onChange={d => onFiltersChange({ ...filters, nearest_deadline_to: formatToYYYYMMDD(d) })}
                />

                {/* Сортировка */}
                <JoySelectField
                  label="Сортировка"
                  placeholder="Выберите поле"
                  value={filters.sort_by || null}
                  onChange={val => onFiltersChange({ ...filters, sort_by: val || '' })}
                  options={SORT_OPTIONS}
                />

                {/* Порядок сортировки */}
                <JoySelectField
                  label="Порядок сортировки"
                  placeholder="Выберите порядок"
                  value={filters.sort_order || null}
                  onChange={val => onFiltersChange({ ...filters, sort_order: val || 'desc' })}
                  options={SORT_ORDER_OPTIONS}
                />

              </div>

              {/* ─── Кнопки применения ─── */}
              <div className="mt-6 pt-5 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={onReset}
                  disabled={isResetDisabled}
                  className={`px-6 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                    isResetDisabled
                      ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed opacity-70'
                      : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-100 shadow-sm'
                  }`}
                >
                  Сбросить
                </button>
                <button
                  onClick={onApply}
                  disabled={isApplyDisabled}
                  className={`px-6 py-2 rounded-lg text-white text-sm font-semibold transition-all duration-200 shadow-sm ${
                    isApplyDisabled
                      ? 'bg-blue-600 opacity-50 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  Применить
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchAndFilters;